import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Row,
  Col,
  Card,
  Tag,
  Spin,
  Alert,
  Radio,
  List,
  Avatar,
  Space,
  Button,
  Input,
  Layout,
} from 'antd';
import {
  EnvironmentOutlined,
  PhoneOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { getPlaces } from '../services/placesService';

const { Title, Text } = Typography;
const { Search: SearchInput } = Input;

function PlaceCard({ place }) {
  return (
    <Card
      hoverable
      cover={
        <img
          alt={place.name}
          src={place.image_url || 'https://source.unsplash.com/random?city'}
          style={{ height: 240, objectFit: 'cover' }}
        />
      }
      style={{
        height: '100%',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Card.Meta
        title={<Title level={4}>{place.name}</Title>}
        description={
          <Space direction="vertical" style={{ width: '100%' }}>
            <Tag>{place.categories?.name || 'Uncategorized'}</Tag>

            <Text type="secondary">{place.description}</Text>

            {place.address && (
              <Space>
                <EnvironmentOutlined />
                <Text>{place.address}</Text>
              </Space>
            )}

            {place.phone && (
              <Space>
                <PhoneOutlined />
                <Text>{place.phone}</Text>
              </Space>
            )}

            {place.website_url && (
              <Space>
                <GlobalOutlined />
                <a
                  href={
                    place.website_url.includes('http')
                      ? place.website_url
                      : `http://${place.website_url}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Website
                </a>
              </Space>
            )}
          </Space>
        }
      />
    </Card>
  );
}

function PlaceListItem({ place }) {
  return (
    <List.Item
      style={{
        marginBottom: 16,
        border: '4px solid black',
        borderRadius: 8,
        backgroundColor: 'white',
        padding: 16,
      }}
      extra={
        <Space>
          {place.address && <Button type="text" icon={<EnvironmentOutlined />} />}
          {place.phone && <Button type="text" icon={<PhoneOutlined />} />}
          {place.website_url && (
            <Button
              type="text"
              icon={<GlobalOutlined />}
              href={
                place.website_url.includes('http')
                  ? place.website_url
                  : `http://${place.website_url}`
              }
              target="_blank"
            />
          )}
        </Space>
      }
    >
      <List.Item.Meta
        avatar={
          <Avatar
            shape="square"
            size={100}
            src={place.image_url || 'https://source.unsplash.com/random?city'}
          />
        }
        title={<Title level={5}>{place.name}</Title>}
        description={
          <Space direction="vertical">
            <Tag>{place.categories?.name || 'Uncategorized'}</Tag>
            <Text type="secondary">{place.description}</Text>
          </Space>
        }
      />
    </List.Item>
  );
}

function Places() {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category');

  const filteredPlaces = useMemo(() => {
    return places.filter(place => {
      const matchesSearch =
        place.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        place.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        place.categories?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = !categoryId || place.category_id === categoryId;

      return matchesSearch && matchesCategory;
    });
  }, [places, searchTerm, categoryId]);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewType(newView);
    }
  };

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const data = await getPlaces();
        setPlaces(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlaces();
  }, []);

  if (loading) {
    return (
      <div sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <div />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <Layout style={{ background: '#fff', padding: '24px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2}>Places</Title>
            </Col>
            <Col>
              <Radio.Group
                value={viewType}
                onChange={e => setViewType(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="grid">
                  <AppstoreOutlined /> Grid
                </Radio.Button>
                <Radio.Button value="list">
                  <UnorderedListOutlined /> List
                </Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <SearchInput
            placeholder="Search places..."
            allowClear
            size="large"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ maxWidth: 600, width: '100%' }}
          />
        </Col>

        <Col span={24}>
          {loading ? (
            <Row justify="center" style={{ padding: '48px' }}>
              <Spin size="large" />
            </Row>
          ) : error ? (
            <Alert message="Error" description={error} type="error" showIcon />
          ) : filteredPlaces.length === 0 ? (
            <Alert
              message="No places found"
              description="Try adjusting your search criteria"
              type="info"
              showIcon
            />
          ) : viewType === 'grid' ? (
            <Row gutter={[24, 24]}>
              {filteredPlaces.map(place => (
                <Col xs={24} sm={12} md={8} lg={6} key={place.id}>
                  <PlaceCard place={place} />
                </Col>
              ))}
            </Row>
          ) : (
            <List
              itemLayout="vertical"
              size="large"
              dataSource={filteredPlaces}
              renderItem={place => <PlaceListItem place={place} />}
            />
          )}
        </Col>
      </Row>
    </Layout>
  );
}

export default Places;
